import { useState, useRef, useCallback, useEffect } from "react";
import { Globe, FileText, AlignLeft, Upload, X, Loader2, ArrowRight } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Input } from "@/modern/components/ui/input";
import { cn } from "@/modern/utils";
import { useBoardContextContext } from "../context/BoardContextContext";

type Tab = "url" | "pdf" | "note";

const TABS: { id: Tab; label: string; icon: typeof Globe }[] = [
  { id: "url", label: "Website URL", icon: Globe },
  { id: "pdf", label: "Upload PDF", icon: FileText },
  { id: "note", label: "Add a note", icon: AlignLeft },
];

const NO_KEY_TITLE = "An OpenAI API key is required to use this feature";

export const SetupForm = () => {
  const {
    handleSetupSubmit,
    isLoadingMapUrl,
    boardContextRecord,
    hasOpenAIKey,
    hasContextView,
    hasDraftQuestions,
    goToContextView,
    goToQuestions,
    step,
  } = useBoardContextContext();

  const [activeTab, setActiveTab] = useState<Tab>("url");
  const [websiteUrl, setWebsiteUrl] = useState(boardContextRecord?.websiteUrl ?? "");
  const [businessDescription, setBusinessDescription] = useState(
    boardContextRecord?.businessDescription ?? ""
  );
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSubmitting || step !== "setup") return;
    setWebsiteUrl(boardContextRecord?.websiteUrl ?? "");
    setBusinessDescription(boardContextRecord?.businessDescription ?? "");
    setFile(null);
  }, [boardContextRecord?.websiteUrl, boardContextRecord?.businessDescription, step, isSubmitting]);

  const hasAnyInput = websiteUrl.trim() || businessDescription.trim() || file;
  const isDisabled = isSubmitting || isLoadingMapUrl || !hasOpenAIKey;
  const canSubmit = hasAnyInput && !isDisabled;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await handleSetupSubmit({
        websiteUrl: websiteUrl.trim(),
        businessDescription: businessDescription.trim(),
        file,
        fileName: file?.name ?? "",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped && (dropped.type === "application/pdf" || dropped.type === "text/plain")) {
      setFile(dropped);
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) setFile(selected);
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className={cn("px-6 pb-5 w-full", hasOpenAIKey && "pt-5")}>
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2.5 mb-1">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <Globe className="h-3.5 w-3.5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground">Tell us about your business</h2>
          </div>
          <p className="text-xs text-muted-foreground ml-10">
            Provide one or more sources — we'll read them and build a Context document for your board.
          </p>
        </div>

        {/* Source type tabs */}
        <div className="flex gap-1 p-1 bg-muted/50 rounded-lg mb-4 w-fit">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              disabled={!hasOpenAIKey}
              className={cn(
                "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-all duration-150",
                activeTab === id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* URL tab */}
          {activeTab === "url" && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Website URL</label>
              <p className="text-xs text-muted-foreground">
                We'll map the site and let you pick which pages to analyse.
              </p>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourbusiness.com"
                  disabled={isDisabled}
                  className="pl-9"
                />
              </div>
            </div>
          )}

          {/* PDF tab */}
          {activeTab === "pdf" && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Upload a document</label>
              <p className="text-xs text-muted-foreground">PDF or .txt files only.</p>

              {file ? (
                <div className="flex items-center gap-3 p-3 border border-border rounded-lg bg-muted/30">
                  <FileText className="h-5 w-5 text-primary shrink-0" />
                  <span className="text-sm text-foreground flex-1 truncate">{file.name}</span>
                  <button
                    type="button"
                    onClick={() => setFile(null)}
                    disabled={!hasOpenAIKey}
                    className="text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50 disabled:pointer-events-none"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div
                  onDragOver={hasOpenAIKey ? (e) => { e.preventDefault(); setIsDragging(true); } : undefined}
                  onDragLeave={hasOpenAIKey ? () => setIsDragging(false) : undefined}
                  onDrop={hasOpenAIKey ? handleFileDrop : undefined}
                  onClick={hasOpenAIKey ? () => fileInputRef.current?.click() : undefined}
                  className={cn(
                    "flex flex-col items-center justify-center gap-3 p-8 border-2 border-dashed rounded-lg transition-all duration-150",
                    hasOpenAIKey ? "cursor-pointer" : "cursor-not-allowed opacity-50",
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-border bg-muted/20",
                    hasOpenAIKey && !isDragging && "hover:border-primary/50 hover:bg-muted/40"
                  )}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <Upload className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-foreground">Drop your file here</p>
                    <p className="text-xs text-muted-foreground mt-0.5">or click to browse</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.txt"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              )}
            </div>
          )}

          {/* Note tab */}
          {activeTab === "note" && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Business description</label>
              <p className="text-xs text-muted-foreground">
                Describe your business: services, target customers, location, what makes you different.
              </p>
              <textarea
                value={businessDescription}
                onChange={(e) => setBusinessDescription(e.target.value)}
                placeholder="We're a roofing company serving the greater Boston area, specialising in commercial flat roofs and residential shingle replacements..."
                disabled={isDisabled}
                rows={6}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y"
              />
            </div>
          )}

          {/* Summary of other filled sources */}
          <div className="flex flex-wrap gap-2">
            {activeTab !== "url" && websiteUrl && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                <Globe className="h-3 w-3" />
                {websiteUrl}
              </span>
            )}
            {activeTab !== "pdf" && file && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                <FileText className="h-3 w-3" />
                {file.name}
              </span>
            )}
            {activeTab !== "note" && businessDescription && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                <AlignLeft className="h-3 w-3" />
                Note added
              </span>
            )}
          </div>

          <div className="flex items-center justify-between pt-1 gap-4">
            {!hasAnyInput && (
              <p className="text-xs text-muted-foreground">
                Provide at least one source to continue.
              </p>
            )}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {hasContextView ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToContextView}
                  disabled={isDisabled}
                >
                  See Business Context
                </Button>
              ) : hasDraftQuestions ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={goToQuestions}
                  disabled={isDisabled}
                >
                  Submit Questions
                </Button>
              ) : null}
              <Button
                type="submit"
                disabled={!canSubmit}
                title={!hasOpenAIKey ? NO_KEY_TITLE : undefined}
                className="gap-2"
              >
                {isSubmitting || isLoadingMapUrl ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {isLoadingMapUrl ? "Mapping site..." : "Saving..."}
                  </>
                ) : (
                  <>
                    {websiteUrl.trim() ? "Map & pick pages" : "Generate questions"}
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
